// ProtocolsPage.jsx
import "./ProtocolsPage.scss";

import { Badge, Col, Form, Row } from "react-bootstrap";
import React, { useEffect, useMemo, useState } from "react";
import { faAdd, faSearch } from "@fortawesome/free-solid-svg-icons";

import AppLinkButton from "../../../../../commons/buttons/AppLinkButton";
import AppLoading from "../../../../../commons/AppLoading";
import ConfirmModal from "../../../../../commons/modal/ConfirmModal";
import CustomCard from "../../../../../commons/cards/Card";
import EmptyState from "../../../../../commons/EmptyState";
import FloatingButton from "../../../../../commons/buttons/FloatingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../../../../../commons/HallAppToast";
import ModalProtocolCreateEdit from "./ModalProtocolCreateEdit";
import ProtocolAPI from "../../api/ProtocolAPI";
import { formatDate } from "../../../../../commons/util/HallAppUtil";

const protocolAPI = new ProtocolAPI();

const STATUS_OPTIONS = [
  { value: "ALL", label: "Todos" },
  { value: "ACTIVE", label: "Ativos" },
  { value: "INACTIVE", label: "Inativos" },
];

const MODE_OPTIONS = [
  { value: "ALL", label: "Todos" },
  { value: "CUSTOM", label: "Custom" },
  { value: "PDF", label: "PDF" },
];

export default function ProtocolsPage() {
  const [isLoading, setIsLoading] = useState(true);

  const [modalAddProtocol, setModalAddProtocol] = useState(false);
  const [editingProtocol, setEditingProtocol] = useState(null);

  const [rows, setRows] = useState([]);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [mode, setMode] = useState("ALL");

  const [showModalDelete, setShowModalDelete] = useState(false);
  const [isLoadingModalDelete, setIsLoadingModalDelete] = useState(false);
  const [selectedToDelete, setSelectedToDelete] = useState(null);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchAll() {
    try {
      setIsLoading(true);
      const resp = await protocolAPI.findAll();
      setRows(Array.isArray(resp?.data) ? resp.data : []);
    } catch (_) {
      setRows([]);
      HallAppToast.genericError();
    } finally {
      setIsLoading(false);
    }
  }

  const protocols = useMemo(() => {
    const text = q.trim().toLowerCase();

    return (rows || []).filter((p) => {
      const matchesText =
        !text ||
        p.name?.toLowerCase().includes(text) ||
        p.description?.toLowerCase().includes(text) ||
        (p.tags || []).some((t) => (t || "").toLowerCase().includes(text));

      const matchesStatus = status === "ALL" ? true : p.status === status;
      const matchesMode = mode === "ALL" ? true : p.mode === mode;

      return matchesText && matchesStatus && matchesMode;
    });
  }, [rows, q, status, mode]);

  function badgeForStatus(p) {
    if (p.status === "ACTIVE") return { bg: "success", label: "Ativo" };
    return { bg: "secondary", label: "Inativo" };
  }

  function badgeForMode(p) {
    if (p.mode === "PDF") return { bg: "danger", label: "PDF" };
    return { bg: "primary", label: "Custom" };
  }


  function downloadPdf(p, e) {
    e?.stopPropagation?.();

    const url = p?.documents?.[0]?.fileUrl;
    if (!url) return;

    const fileName =
      p?.documents?.[0]?.fileName ||
      `${p?.name || "protocolo"}.pdf`;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Erro ao baixar arquivo");
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`;
        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        // fallback: abre em nova aba se o download falhar
        window.open(url, "_blank", "noopener,noreferrer");
      });
  }


  function onEdit(p, e) {
    e?.stopPropagation?.();
    setEditingProtocol(p);
    setModalAddProtocol(true);
  }

  function onAskDelete(p, e) {
    e?.stopPropagation?.();
    setSelectedToDelete(p);
    setShowModalDelete(true);
  }

  async function executeDeletion() {
    if (!selectedToDelete?.id) return;

    setIsLoadingModalDelete(true);
    try {
      // ajuste o nome do método conforme seu ProtocolAPI (delete/remove/deleteById...)
      await protocolAPI.delete(selectedToDelete.id);

      setShowModalDelete(false);
      setSelectedToDelete(null);
      fetchAll();
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingModalDelete(false);
    }
  }

  return (
    <div className="container protocols-page">
      <div className="protocols-page__header">
        <div className="protocols-page__title">Protocolos</div>
        <div className="protocols-page__subtitle">
          Padronize tratamentos e gere sessões com consistência.
        </div>
      </div>

      {/* Filtros */}
      <div className="protocols-page__filters">
        <div className="protocols-page__search">
          <FontAwesomeIcon
            icon={faSearch}
            className="protocols-page__searchIcon"
          />
          <Form.Control
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, descrição ou tag..."
          />
        </div>

        <Row className="g-2" style={{ margin: 0, width: "100%" }}>
          <Col xs={6} md={3}>
            <Form.Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col xs={6} md={3}>
            <Form.Select value={mode} onChange={(e) => setMode(e.target.value)}>
              {MODE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </div>

      {isLoading ? (
        <div className="protocols-page__loading">
          <AppLoading />
        </div>
      ) : protocols.length === 0 ? (
        <EmptyState
          title="Nenhum protocolo encontrado"
          description="Crie seu primeiro protocolo para padronizar os atendimentos."
          actionLabel="Criar protocolo"
          onAction={() => setModalAddProtocol(true)}
        />
      ) : (
        <Row className="g-3" style={{ margin: 0, width: "100%" }}>
          {protocols.map((p) => {
            const bStatus = badgeForStatus(p);
            const bMode = badgeForMode(p);
            const docsCount = (p.documents || []).length;

            return (
              <Col xs={12} sm={6} md={4} lg={4} key={p.id}>
                <CustomCard className="protocol-card">
                  <div className="protocol-card__top">
                    <div className="protocol-card__badges">
                      <Badge bg={bStatus.bg} className="protocol-card__badge">
                        {bStatus.label}
                      </Badge>

                      <Badge
                        bg={bMode.bg}
                        className="protocol-card__badge protocol-card__badge--mode"
                      >
                        {bMode.label}
                      </Badge>
                    </div>

                    <div className="protocol-card__meta">
                      <span className="protocol-card__metaItem">
                        {docsCount > 0 ? `${docsCount} anexo(s)` : "Sem anexos"}
                      </span>
                      <span className="protocol-card__dot">•</span>
                      <span className="protocol-card__metaItem">
                        Atualizado em {formatDate(p.updateDt || p.createDt)}
                      </span>
                    </div>
                  </div>

                  <div className="protocol-card__title">{p.name}</div>

                  {p.description ? (
                    <div className="protocol-card__desc">{p.description}</div>
                  ) : null}

                  {(p.tags || []).length > 0 ? (
                    <div className="protocol-card__tags">
                      {(p.tags || []).slice(0, 4).map((t) => (
                        <span key={t} className="protocol-card__tag">
                          #{t}
                        </span>
                      ))}
                      {(p.tags || []).length > 4 ? (
                        <span className="protocol-card__tagMore">
                          +{(p.tags || []).length - 4}
                        </span>
                      ) : null}
                    </div>
                  ) : null}

                  <hr></hr>
                  <div className="protocol-card__footer">
                    <div className="protocol-card__left">
                      {p.mode === "PDF" && p.documents?.[0]?.fileUrl ? (
                        <AppLinkButton
                          name="baixar pdf"
                          onClick={(e) => downloadPdf(p, e)}
                        />
                      ) : null}
                    </div>

                    <div className="protocol-card__right">
                      <AppLinkButton name="editar" onClick={(e) => onEdit(p, e)} />
                      <AppLinkButton
                        name="excluir"
                        onClick={(e) => onAskDelete(p, e)}
                        className="protocol-card__deleteBtn"
                      />
                    </div>
                  </div>
                </CustomCard>
              </Col>
            );
          })}

          <div style={{ height: "calc(50px + 72px)" }} />
        </Row>
      )}

      <FloatingButton icon={faAdd} onClick={() => {
        setEditingProtocol(null);
        setModalAddProtocol(true);
      }} />

      {modalAddProtocol && (
        <ModalProtocolCreateEdit
          show={modalAddProtocol}
          protocolToEdit={editingProtocol}
          callProtocols={() => fetchAll()}
          closeShow={() => {
            setModalAddProtocol(false);
            setEditingProtocol(null);
          }}
        />
      )}

      {showModalDelete && (
        <ConfirmModal
          show={showModalDelete}
          title="Excluir"
          isLoading={isLoadingModalDelete}
          confirm={executeDeletion}
          closeModal={() => {
            setShowModalDelete(false);
            setSelectedToDelete(null);
          }}
        >
          Deseja remover o protocolo{" "}
          <span style={{ fontWeight: "bold" }}>{selectedToDelete?.name}</span>?
        </ConfirmModal>
      )}
    </div>
  );
}
